import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import type { CitationSource } from '@ctw/shared/content/fact-extraction/use-citation-sources';

// A field included in the export: any field not explicitly excluded,
// documented or not. This is the single source of truth for export scope —
// the CSV, the summary counts, and the modal all derive from it.
export interface ExportField {
  id: string;
  groupLabel: string;
  label: string;
  value: string;
  isDocumented: boolean;
  citationIds: Array<number>;
}

// How a cited source document can participate in the export. Values are
// produced by use-export-documents (classification against the resolved
// DocumentReference); the type lives here so pure consumers avoid importing
// the hook module.
export interface ExportDocumentInfo {
  binaryId: string;
  title: string;
  status: 'pdf' | 'ccda' | 'unsupported' | 'unresolved';
  // Human-readable explanation, set when not deliverable. Shown in the modal
  // and recorded in the manifest.
  reason?: string;
  // Set for deliverable documents (needed to call /v1/cda/pdf).
  documentReferenceId?: string;
  // Formatted date from the DocumentReference, used in zip file names.
  date?: string;
  // Citation refs ("ResourceType/id") this document backs — the join key
  // between the CSV's citations column and the zip's documents/ entries.
  // Populated by the export modal at download time.
  citationRefs?: Array<string>;
}

export const isDeliverable = (doc: ExportDocumentInfo): boolean =>
  doc.status === 'pdf' || doc.status === 'ccda';

export function collectExportFields({
  groups,
  excludedIds,
}: {
  groups: Array<MappedFactGroup>;
  excludedIds: Array<string>;
}): Array<ExportField> {
  const excluded = new Set(excludedIds);
  return groups.flatMap((group) =>
    group.facts
      .filter((fact) => !excluded.has(fact.id))
      .map((fact) => ({
        id: fact.id,
        groupLabel: group.label,
        label: fact.label,
        value: fact.value,
        isDocumented: fact.isDocumented,
        citationIds: fact.citationIds,
      })),
  );
}

export interface ExportGroupSummary {
  label: string;
  includedCount: number;
  documentedIncludedCount: number;
}

export interface ExportSummary {
  includedFieldsCount: number;
  excludedFieldsCount: number;
  documentedIncludedCount: number;
  groupSummaries: Array<ExportGroupSummary>;
  // Deduped source documents cited by included fields, with deliverability.
  includedSources: Array<ExportDocumentInfo>;
  deliverableSourcesCount: number;
  // Deduped source documents cited by any field in the report.
  totalSourcesCount: number;
}

const EMPTY_DOCUMENTS: ReadonlyMap<string, ExportDocumentInfo> = new Map();

/**
 * Summarizes what an Atlas export would contain given the current exclusions.
 * A field is included unless explicitly excluded — undocumented fields export
 * as empty rows. Only citations that resolve to an actual source document
 * (kind 'document') count as sources, deduped by binaryId; deliverability
 * comes from `exportDocumentsByBinaryId` (callers without the resolution, like
 * the page-level badge, may omit it — sources then read as unresolved).
 */
export function computeExportSummary({
  groups,
  excludedIds,
  sourceByCitationId,
  exportDocumentsByBinaryId = EMPTY_DOCUMENTS,
}: {
  groups: Array<MappedFactGroup>;
  excludedIds: Array<string>;
  sourceByCitationId: ReadonlyMap<number, CitationSource>;
  exportDocumentsByBinaryId?: ReadonlyMap<string, ExportDocumentInfo>;
}): ExportSummary {
  const excluded = new Set(excludedIds);
  const allSourcesByBinaryId = new Map<string, string>();
  const includedBinaryIds = new Set<string>();
  let includedFieldsCount = 0;
  let excludedFieldsCount = 0;
  let documentedIncludedCount = 0;

  const groupSummaries = groups.map((group) => {
    let includedCount = 0;
    let groupDocumentedIncludedCount = 0;
    for (const fact of group.facts) {
      const isExcluded = excluded.has(fact.id);
      if (isExcluded) {
        excludedFieldsCount += 1;
      } else {
        includedFieldsCount += 1;
        includedCount += 1;
        if (fact.isDocumented) {
          documentedIncludedCount += 1;
          groupDocumentedIncludedCount += 1;
        }
      }
      for (const citationId of fact.citationIds) {
        const source = sourceByCitationId.get(citationId);
        if (source?.kind === 'document') {
          allSourcesByBinaryId.set(source.binaryId, source.label);
          if (!isExcluded) {
            includedBinaryIds.add(source.binaryId);
          }
        }
      }
    }
    return {
      label: group.label,
      includedCount,
      documentedIncludedCount: groupDocumentedIncludedCount,
    };
  });

  const includedSources = [...includedBinaryIds].map(
    (binaryId): ExportDocumentInfo =>
      exportDocumentsByBinaryId.get(binaryId) ?? {
        binaryId,
        title: allSourcesByBinaryId.get(binaryId) ?? 'Unknown document',
        status: 'unresolved',
        reason: 'No document record found for this source',
      },
  );

  return {
    includedFieldsCount,
    excludedFieldsCount,
    documentedIncludedCount,
    groupSummaries,
    includedSources,
    deliverableSourcesCount: includedSources.filter(isDeliverable).length,
    totalSourcesCount: allSourcesByBinaryId.size,
  };
}
